export interface GrayFrame {
    data: Uint8ClampedArray;
    width: number;
    height: number;
}

export interface ViewportSize {
    width: number;
    height: number;
}

interface CanvasWithContext {
    canvas: HTMLCanvasElement;
    ctx: CanvasRenderingContext2D;
}

// The video is rendered full-screen with `object-fit: cover`, i.e. scaled up and center-cropped.
// Mobile cameras typically deliver landscape frames while the phone is held portrait, so most of
// the raw frame is never visible on screen. All live captures are therefore cropped to exactly
// this visible region BEFORE upload/tracking: the AI sees precisely what the user is aiming at,
// every returned box is directly a screen-relative coordinate (no back-mapping), and the upload
// resolution is spent on the meter instead of on cropped-away margins.
export const coverCropRegion = (
    videoWidth: number,
    videoHeight: number,
    viewport: ViewportSize | null | undefined,
): { sx: number; sy: number; sw: number; sh: number } => {
    if (!viewport || !viewport.width || !viewport.height || !videoWidth || !videoHeight) {
        return { sx: 0, sy: 0, sw: videoWidth, sh: videoHeight };
    }
    const scale = Math.max(viewport.width / videoWidth, viewport.height / videoHeight);
    const sw = viewport.width / scale;
    const sh = viewport.height / scale;
    return { sx: (videoWidth - sw) / 2, sy: (videoHeight - sh) / 2, sw, sh };
};

// The AiReadingScreen renders edge-to-edge (fixed inset-0), so the visible viewport is the window.
export const visibleViewport = (): ViewportSize => ({ width: window.innerWidth, height: window.innerHeight });

// These helpers run up to ~30×/s (tracker). Allocating a fresh canvas per call piles up backing
// stores faster than the GC reclaims them — iOS Safari enforces a hard canvas-memory budget and
// visibly throttles once it fills up (a completed assistant run leaves the next one sluggish).
// Two pooled canvases are reused instead: 'small' for the grayscale hot path, 'large' for the
// occasional JPEG captures. The 2d context is re-fetched per call (same object in browsers).
type CanvasPoolSlot = 'small' | 'large';
const canvasPool: Partial<Record<CanvasPoolSlot, HTMLCanvasElement>> = {};

const pooledCanvas = (slot: CanvasPoolSlot, width: number, height: number): HTMLCanvasElement => {
    let canvas = canvasPool[slot];
    if (!canvas) {
        canvas = document.createElement('canvas');
        canvasPool[slot] = canvas;
    }
    if (canvas.width !== width) {
        canvas.width = width;
    }
    if (canvas.height !== height) {
        canvas.height = height;
    }
    return canvas;
};

const drawSourceRegionToCanvas = (
    video: HTMLVideoElement,
    source: { sx: number; sy: number; sw: number; sh: number },
    targetWidth: number,
    targetHeight: number,
    slot: CanvasPoolSlot,
): CanvasWithContext | null => {
    if (!video || video.readyState < 2 || !video.videoWidth || !video.videoHeight) {
        return null;
    }
    const canvas = pooledCanvas(slot, targetWidth, targetHeight);
    const ctx = canvas.getContext('2d', { willReadFrequently: true } as any) as CanvasRenderingContext2D | null;
    if (!ctx) {
        return null;
    }
    ctx.drawImage(video, source.sx, source.sy, source.sw, source.sh, 0, 0, targetWidth, targetHeight);
    return { canvas, ctx };
};

const drawVideoToCanvas = (
    video: HTMLVideoElement,
    targetWidth: number,
    targetHeight: number,
    viewport: ViewportSize | null | undefined,
    slot: CanvasPoolSlot,
): CanvasWithContext | null => {
    if (!video || video.readyState < 2 || !video.videoWidth || !video.videoHeight) {
        return null;
    }
    return drawSourceRegionToCanvas(video, coverCropRegion(video.videoWidth, video.videoHeight, viewport), targetWidth, targetHeight, slot);
};

// Captures a viewport-relative region (e.g. the tracked meter box plus padding) at the NATIVE
// video resolution of that region — no prior downscale, so the crop carries every pixel the
// camera has on the meter. Returns the JPEG plus the region that was actually captured (it may
// differ from the requested one when clamping to the video bounds), which callers need to map
// the AI's crop-relative boxes back into viewport coordinates.
export const captureViewportRegion = (
    video: HTMLVideoElement,
    region: { x: number; y: number; w: number; h: number },
    maxDimension: number,
    quality: number,
    viewport?: ViewportSize | null,
): { image: string; region: { x: number; y: number; w: number; h: number } } | null => {
    if (!video || video.readyState < 2 || !video.videoWidth || !video.videoHeight) {
        return null;
    }
    const crop = coverCropRegion(video.videoWidth, video.videoHeight, viewport);
    const requestedX = crop.sx + region.x * crop.sw;
    const requestedY = crop.sy + region.y * crop.sh;
    const x0 = Math.max(0, requestedX);
    const y0 = Math.max(0, requestedY);
    const x1 = Math.min(video.videoWidth, requestedX + region.w * crop.sw);
    const y1 = Math.min(video.videoHeight, requestedY + region.h * crop.sh);
    const sw = x1 - x0;
    const sh = y1 - y0;
    if (sw < 16 || sh < 16) {
        return null;
    }
    const scale = Math.min(1, maxDimension / Math.max(sw, sh));
    const result = drawSourceRegionToCanvas(video, { sx: x0, sy: y0, sw, sh }, Math.round(sw * scale), Math.round(sh * scale), 'large');
    if (!result) {
        return null;
    }
    const dataUri = result.canvas.toDataURL('image/jpeg', quality);
    if (!dataUri.startsWith('data:image/jpeg')) {
        return null;
    }
    return {
        image: dataUri,
        region: { x: (x0 - crop.sx) / crop.sw, y: (y0 - crop.sy) / crop.sh, w: sw / crop.sw, h: sh / crop.sh },
    };
};

export const captureFrame = (video: HTMLVideoElement, maxDimension: number, quality: number, viewport?: ViewportSize | null): string | null => {
    if (!video || video.readyState < 2 || !video.videoWidth || !video.videoHeight) {
        return null;
    }
    const { sw, sh } = coverCropRegion(video.videoWidth, video.videoHeight, viewport);
    const scale = Math.min(1, maxDimension / Math.max(sw, sh));
    const width = Math.round(sw * scale);
    const height = Math.round(sh * scale);
    const result = drawVideoToCanvas(video, width, height, viewport, 'large');
    if (!result) {
        return null;
    }
    const dataUri = result.canvas.toDataURL('image/jpeg', quality);
    return dataUri.startsWith('data:image/jpeg') ? dataUri : null;
};

export const grayscaleFrame = (video: HTMLVideoElement, width: number, viewport?: ViewportSize | null): GrayFrame | null => {
    if (!video || video.readyState < 2 || !video.videoWidth || !video.videoHeight) {
        return null;
    }
    const { sw, sh } = coverCropRegion(video.videoWidth, video.videoHeight, viewport);
    const height = Math.max(1, Math.round((sh / sw) * width));
    const result = drawVideoToCanvas(video, width, height, viewport, 'small');
    if (!result) {
        return null;
    }
    const { data } = result.ctx.getImageData(0, 0, width, height);
    const gray = new Uint8ClampedArray(width * height);
    for (let i = 0; i < gray.length; i++) {
        const o = i * 4;
        gray[i] = 0.299 * data[o] + 0.587 * data[o + 1] + 0.114 * data[o + 2];
    }
    return { data: gray, width, height };
};

export const meanAbsDiff = (a: GrayFrame, b: GrayFrame): number => {
    if (a.width !== b.width || a.height !== b.height) {
        return Infinity;
    }
    let sum = 0;
    for (let i = 0; i < a.data.length; i++) {
        sum += Math.abs(a.data[i] - b.data[i]);
    }
    return sum / a.data.length;
};
